import { defineStore, storeToRefs } from 'pinia';
import { ref } from 'vue';
// eslint-disable-next-line import/no-cycle
import { setAuthState } from '../composables/auth';
import { getProductsPlan } from '../api/subscription';
import { update as updateProfile } from '../api/profile';
import { usePremiumNudgeStore } from './nudge-premium.store';
import { useFeatureUsageStore } from './feature-usage.store';
import { useSubscriptionTrackerStore } from './subscription-tracker.store';
import { user } from '../api/auth';
import { BASIC_FEATURES, PREMIUM_FEATURES } from '../constants/subscription-benefits';
import { BILLING_CYCLE_MONTHLY, BILLING_CYCLE_SIX_MONTH } from '../utils/premium-pricing';

export const useSubscriptionPlanStore = defineStore('plan', () => {
	const subscriptionTrackerStore = useSubscriptionTrackerStore();
	const featuresUsageExceedStore = useFeatureUsageStore();
	const premiumNudgeStore = usePremiumNudgeStore();
	const { showModal } = storeToRefs(premiumNudgeStore);
	const { getUser, setUser } = setAuthState();
	const userPlan = ref(getUser()?.current_plan ?? null);
	const isOnGracePeriod = ref(getUser()?.on_grace_period);
	const isSubscribe = ref(false);
	const hasSubscription = ref(false);
	// EN-928: who provides the user's premium, when it is not their own paid plan.
	// Refs rather than computeds on purpose — getUser() reads and JSON.parses localStorage and
	// is not reactive, so a computed would evaluate once and never see a later user refresh.
	// These are kept in step with isSubscribe/hasSubscription inside updateSubscriptionFlags().
	const premiumProvider = ref(null);
	const isOrgProvidedPremium = ref(false);
	const selectedOption = ref(null);
	const subscriptionPlans = ref([]);
	const premiumPlans = ref([]);
	const stripeKey = import.meta.env.VITE_STRIPE_KEY;
	const stripe = ref(null);
	const isStripeLoaded = ref(false);
	const discountCoupon = ref(null);
	const plan = ref([
		{
			name: 'Basic',
			default_price: 'price_free',
			features: BASIC_FEATURES
		}, {
			name: 'Premium',
			default_price: 'price_premium',
			features: PREMIUM_FEATURES
		}
	]);

	// EN-1026: the premium plan as one checkout option per billing cycle. `six_month` is null
	// whenever God Mode is not on sale in the user's currency, which hides the cycle toggle.
	const premiumOptions = ref({ monthly: null, six_month: null });
	const premiumPerk = ref(null);

	const fetchPlan = (defaultPrice) => {
		return plan.value.find((el) => el.default_price === defaultPrice);
	};

	/**
	 * The premium product can hold several prices, so the API names its monthly one in
	 * `billing.monthly`; `prices[0]` is only a fallback for a response without that block.
	 */
	const primaryPrice = (product) => {
		const monthly = product.billing?.monthly;

		if (!monthly) {
			return product.prices?.[0];
		}

		return { id: monthly.price_id, unit_amount: monthly.unit_amount, currency: monthly.currency };
	};

	const buildPremiumOptions = (monthlyPlan, billing) => {
		const sixMonth = billing?.six_month;

		if (!monthlyPlan) {
			return { monthly: null, six_month: null };
		}

		return {
			monthly: { ...monthlyPlan, cycle: BILLING_CYCLE_MONTHLY },
			six_month: sixMonth ? {
				...monthlyPlan,
				cycle: BILLING_CYCLE_SIX_MONTH,
				default_price: sixMonth.price_id,
				amount: sixMonth.unit_amount,
				currency: sixMonth.currency,
				prices: { id: sixMonth.price_id, unit_amount: sixMonth.unit_amount, currency: sixMonth.currency },
				is_intro: !!sixMonth.is_intro,
				regular_amount: sixMonth.regular_unit_amount ?? null,
				intro_ends_on: sixMonth.intro_ends_on ?? null,
			} : null,
		};
	};

	const initPlans = async() => {
		const plans = await getProductsPlan();

		subscriptionPlans.value = (plans || [])
			.sort((a, b) => a.metadata.price - b.metadata.price)
			.map((e) => {
				const price = primaryPrice(e);

				return {
					id: e.id,
					default_price: price?.id,
					product: e.product,
					nickname: e.nickname,
					title: e.metadata.title,
					type: e.metadata.plan,
					amount: price?.unit_amount,
					cost: e.metadata.amount,
					description: e.metadata.description,
					best: e.metadata.best === 'true',
					prices: price,
					currency: price?.currency,
					benefits:  e.metadata.plan ? fetchPlan(e.default_price) : [],
				};
			});
		premiumPlans.value = subscriptionPlans.value.filter((entry) => entry.type === 'premium');

		const premiumBilling = (plans || []).find((product) => product.metadata?.plan === 'premium')?.billing;
		premiumOptions.value = buildPremiumOptions(premiumPlans.value[0], premiumBilling);
		premiumPerk.value = premiumBilling?.perk ?? null;
	};

	const updateSubscriptionFlags = () => {
		const currentUser = getUser();
		const isSoftLocked = currentUser?.grace_period_phase === 'soft_lock';
		hasSubscription.value = currentUser?.current_plan != null || !!currentUser?.payment_failed_at;
		isSubscribe.value = currentUser?.current_plan != null && !isSoftLocked;
		premiumProvider.value = currentUser?.premium_provider ?? null;
		isOrgProvidedPremium.value = premiumProvider.value?.type === 'organization';
	};

	// Initialize subscription flags
	updateSubscriptionFlags();

	const setUserPlan = (currentPlan, onGracePeriod) => {
		if (!currentPlan && getUser()?.grace_period_phase !== 'soft_lock') {
			isSubscribe.value = false;
			hasSubscription.value = false;
			premiumProvider.value = null;
			isOrgProvidedPremium.value = false;
			return;
		};

		userPlan.value = currentPlan;
		isOnGracePeriod.value = onGracePeriod ?? getUser()?.on_grace_period;
		updateSubscriptionFlags();
	};

	const selectOption = (option) => {
		selectedOption.value = option ? { ...option } : null;
	};

	const refresh = async() => {
		const result = await user();
		if (result?.data) {
			setUser(result.data);
			setUserPlan(result?.data?.current_plan);
		}
	};

	const initiateStripe = () => {
		// `Stripe` is the Stripe.js v3 global, loaded by a script tag (utils/stripe.js).
		/* global Stripe */
		stripe.value = Stripe(stripeKey);
		isStripeLoaded.value = true;
	};  
    
	const setCouponCode = (coupon, amount) => {
		discountCoupon.value = coupon || null;

		if (coupon != null) {

			const planAmount = amount;

			let totalDiscount = 0;

			if (discountCoupon.value.percent_off) {
				totalDiscount = planAmount * (discountCoupon.value.percent_off / 100);
			} else if (discountCoupon.value.amount_off) {
				totalDiscount = discountCoupon.value.amount_off / 100; // Convert cents to dollars
			}

			discountCoupon.value['discounted_amount'] = planAmount - totalDiscount;
			discountCoupon.value['original_amount'] = planAmount;
		}
	};

	const handleCloseModal = async() => {
		if (showModal.value === true) {
			premiumNudgeStore.hideModal();
		} else {
			const currentUser = getUser();
			if (hasSubscription.value) {
				// Clear premium nudge flag so it doesn't reappear after refresh
				if (currentUser?.options?.show_premium_nudge === true) {
					setUser({ ...currentUser, options: { ...currentUser.options, show_premium_nudge: false } });
					const updatedUser = await updateProfile({
						options: { ...currentUser.options, show_premium_nudge: false }
					});
					setUser(updatedUser.data.data);
				}
			} else {
				await updateProfile({ options: { ['is_card_abandoned']: true } });
			}
			featuresUsageExceedStore.hideLimitExceed();
			subscriptionTrackerStore.handleCloseModal();
			selectOption();
			setCouponCode(null);
		}
	};
    
	return {
		isSubscribe,
		hasSubscription,
		premiumProvider,
		isOrgProvidedPremium,
		plan,
		userPlan,
		subscriptionPlans,
		selectedOption,
		stripeKey,
		premiumPlans,
		premiumOptions,
		premiumPerk,
		isOnGracePeriod,
		stripe,
		isStripeLoaded,
		discountCoupon,
        
		selectOption,
		initPlans,
		fetchPlan,
		setUserPlan,
		refresh,
		initiateStripe,
		setCouponCode,
		handleCloseModal
	};
});
